import { useState, useCallback } from 'react';
import { referenceDataSyncService } from '../../services/referenceDataSyncService';

export const useTaskPresentations = () => {
  const [productsFromTask, setProductsFromTask] = useState<any[]>([]);

  const mergePriceWithPresentation = useCallback((priceData: any[], presentationData: any[]) => {
    const priceMap = new Map<string, any[]>();

    priceData.forEach((price: any) => {
      const pRefId = price?.productRefId || price?.productRef?.refId || price?.productId;
      if (pRefId) {
        const key = String(pRefId);
        if (!priceMap.has(key)) {
          priceMap.set(key, []);
        }
        priceMap.get(key)!.push(price);
      }
    });

    const updatedPresentations = presentationData.map((product: any) => {
      const pRefId = String(product?.refId);
      const pricesForProduct = priceMap.get(pRefId);

      if (pricesForProduct && pricesForProduct.length > 0) {
        pricesForProduct.sort((a, b) => {
          const tsA = a?.reportedTime || a?.createTime || (a?.isLocal ? Date.now() : 0);
          const tsB = b?.reportedTime || b?.createTime || (b?.isLocal ? Date.now() : 0);
          return tsB - tsA;
        });

        const latestPrice = pricesForProduct[0];

        return {
          ...product,
          priceData: latestPrice,
          priceAdded: true,
        };
      }

      return product;
    });

    setProductsFromTask(updatedPresentations);
  }, []);

  const getInspectionForTask = useCallback(
    async (taskData: any, products: any[]) => {
      try {
        const localInspections = await referenceDataSyncService.getInspectionsByTaskId(
          taskData?.refId
        );

        const localInspectionRefIds =
          localInspections?.map((inspection: any) => inspection?.refId) || [];
        const localPrices =
          (await referenceDataSyncService.getPricesByInspectionIds(localInspectionRefIds)) || [];

        const localInspectionsMap = new Map<string, number>();
        localInspections?.forEach((insp: any) => {
          if (insp?.refId) {
            const ts = insp?.createTime || insp?.date || Date.now();
            localInspectionsMap.set(
              insp.refId,
              typeof ts === 'number' ? ts : new Date(ts).getTime()
            );
          }
        });

        const enrichedLocalPrices = localPrices.map((price: any) => {
          const parentTs = localInspectionsMap.get(price?.inspectionRefId) || 0;
          return {
            ...price,
            reportedTime: price?.reportedTime || price?.createTime || parentTs || Date.now(),
            isLocal: true,
          };
        });

        const remotePrices: any[] = [];
        if (taskData?.inspections && Array.isArray(taskData.inspections)) {
          taskData.inspections.forEach((inspection: any) => {
            const inspTime = inspection?.date || inspection?.createTime || 0;
            const inspTs = typeof inspTime === 'number' ? inspTime : new Date(inspTime).getTime();

            if (inspection?.prices && Array.isArray(inspection.prices)) {
              inspection.prices.forEach((price: any) => {
                remotePrices.push({
                  ...price,
                  reportedTime: price?.reportedTime || price?.createTime || inspTs,
                  isLocal: false,
                });
              });
            }
          });
        }

        const combinedPrices = [...enrichedLocalPrices, ...remotePrices];

        mergePriceWithPresentation(combinedPrices, products);
      } catch (err) {
        console.error('--- [useTaskPresentations] ERROR in getInspectionForTask:', err);
        mergePriceWithPresentation([], products);
      }
    },
    [mergePriceWithPresentation]
  );

  const loadTaskPresentations = useCallback(
    async (taskData: any) => {
      if (!taskData) return;
      const allProducts = await referenceDataSyncService.getPresentations();

      const productData: any =
        (taskData?.productRefIds as string[])?.map((productRefId: string) => {
          const product = allProducts.find((product: any) => product.refId === productRefId);
          return product ? product : { refId: productRefId, name: productRefId };
        }) || [];

      setProductsFromTask(productData);
      await getInspectionForTask(taskData, productData);
    },
    [getInspectionForTask]
  );

  return {
    productsFromTask,
    setProductsFromTask,
    loadTaskPresentations,
    getInspectionForTask,
    mergePriceWithPresentation,
  };
};
